import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Badge, Card, LoadingState } from '../ui';
import api from '../../services/api';
import {
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

const GlobalMessagesFeed = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [ownerRegionFilter, setOwnerRegionFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const buildParams = useCallback(() => {
    const params = { limit: 200 };
    if (searchTerm.trim()) params.search = searchTerm.trim();
    if (ownerFilter.trim()) params.owner = ownerFilter.trim();
    if (ownerRegionFilter.trim()) params.ownerRegion = ownerRegionFilter.trim();
    return params;
  }, [searchTerm, ownerFilter, ownerRegionFilter]);

  const { data, isLoading, error } = useQuery(
    ['globalAgentMessages', searchTerm, ownerFilter, ownerRegionFilter],
    () => api.get('/agents/messages', { params: buildParams() }).then((res) => res.data),
    {
      keepPreviousData: true,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchInterval: 15000,
    }
  );

  const messages = data?.messages || [];
  const total = data?.total || 0;
  const filters = data?.filters || {};
  const hasActiveFilters = Boolean(ownerFilter.trim() || ownerRegionFilter.trim());

  return (
    <Card>
      {/* Header: search + filter toggle */}
      <div className="p-4 border-b border-gray-200 space-y-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by agent, chat, or message text..."
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
              showFilters || hasActiveFilters
                ? 'border-blue-300 bg-blue-50 text-blue-700'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
            title="Filter by owner / region"
          >
            <FunnelIcon className="h-4 w-4" />
            Filters
            {hasActiveFilters ? (
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                {(ownerFilter ? 1 : 0) + (ownerRegionFilter ? 1 : 0)}
              </span>
            ) : null}
          </button>
        </div>

        {/* Filter row (collapsible) */}
        {showFilters && (
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-gray-600 font-medium">Owner:</label>
            <input
              type="text"
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              placeholder="e.g. Люба"
              className="w-40 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
            />
            <label className="text-xs text-gray-600 font-medium ml-2">Region:</label>
            <input
              type="text"
              value={ownerRegionFilter}
              onChange={(e) => setOwnerRegionFilter(e.target.value)}
              placeholder="e.g. Турция"
              className="w-32 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
            />
            {hasActiveFilters && (
              <button
                onClick={() => { setOwnerFilter(''); setOwnerRegionFilter(''); }}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-500 hover:bg-gray-100"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
                Clear
              </button>
            )}
          </div>
        )}

        <div className="flex items-baseline justify-between">
          <p className="text-xs text-gray-500">
            Showing latest {messages.length} of {total} friendly-agent messages, including channel discussions
          </p>
          {hasActiveFilters && (
            <div className="flex items-center gap-2 text-xs text-blue-700">
              {filters.owner && <Badge size="sm" variant="blue">owner: {filters.owner}</Badge>}
              {filters.ownerRegion && <Badge size="sm" variant="blue">region: {filters.ownerRegion}</Badge>}
            </div>
          )}
        </div>
      </div>

      {/* Messages list */}
      {isLoading ? (
        <div className="p-6">
          <LoadingState />
        </div>
      ) : error ? (
        <div className="p-6 text-red-500">Error loading messages: {error.message}</div>
      ) : messages.length === 0 ? (
        <div className="p-12 text-center">
          <ChatBubbleLeftRightIcon className="mx-auto mb-3 h-12 w-12 text-gray-300" />
          <p className="text-gray-500">No observed messages found yet</p>
          <p className="mt-1 text-sm text-gray-400">
            {searchTerm.trim() || ownerFilter.trim()
              ? 'Try adjusting your filters.'
              : 'Friendly-agent messages will appear here; messages from tracked channel discussions are labelled separately.'}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {messages.map((msg) => (
            <div key={msg.id} className="p-4 hover:bg-gray-50 transition-colors">
              {/* Top row: agent + owner info */}
              <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
                {/* Sender link: canonical t.me only. */}
                {msg.sender_link ? (
                  <a
                    href={msg.sender_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-blue-600 hover:underline"
                  >
                    {msg.sender_display || msg.agent_display}
                  </a>
                ) : (
                  <span className="font-semibold text-gray-800">
                    {msg.sender_display || msg.agent_display || 'unknown sender'}
                  </span>
                )}

                {/* Friendly-agent detail remains available without hijacking the Telegram tag link. */}
                {msg.agent_id && (
                  <Link
                    to={`/agents/${msg.agent_id}`}
                    className="text-xs text-gray-500 hover:text-blue-700 hover:underline"
                    title={`Agent ID ${msg.agent_id}`}
                  >
                    agent card
                  </Link>
                )}

                {msg.is_channel_discussion && (
                  <Badge size="sm" variant="blue">Дискуссия канала</Badge>
                )}

                {/* Owner badge */}
                {msg.agent_owner && (
                  <Badge size="sm" variant="yellow">
                    {msg.agent_owner}
                    {msg.agent_owner_region ? ` / ${msg.agent_owner_region}` : ''}
                  </Badge>
                )}

                {/* User ID (raw) */}
                {msg.agent_user_id && (
                  <span className="text-xs text-gray-400 font-mono" title="Telegram user_id">
                    #{msg.agent_user_id}
                  </span>
                )}

                {/* Message timestamp */}
                <span className="ml-auto text-xs text-gray-400">
                  {msg.sent_at ? new Date(msg.sent_at).toLocaleString() : '-'}
                </span>
              </div>

              {/* Chat row with link */}
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                <ChatBubbleLeftRightIcon className="h-3.5 w-3.5 text-gray-400" />
                {msg.message_link ? (
                  <a
                    href={msg.message_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {msg.chat_display}
                    <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="text-gray-600">{msg.chat_display}</span>
                )}

                {/* Chat kind badge */}
                {msg.chat_kind && msg.chat_kind !== 'unknown' && (
                  <Badge size="sm" variant={msg.chat_kind === 'public' ? 'green' : 'gray'}>
                    {msg.chat_kind}
                  </Badge>
                )}

                {msg.is_channel_discussion && msg.discussion_source_username && (
                  msg.discussion_source_link ? (
                    <a
                      href={msg.discussion_source_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-blue-600 hover:underline"
                    >
                      канал @{msg.discussion_source_username.replace(/^@+/, '')}
                    </a>
                  ) : (
                    <span className="text-xs text-gray-600">
                      канал @{msg.discussion_source_username.replace(/^@+/, '')}
                    </span>
                  )
                )}

                {/* Relevant projects: agent links for friendly messages and
                    source-project links for tracked channel discussions. */}
                {Array.isArray(msg.project_names) && msg.project_names.length > 0 ? (
                  <>
                    {msg.project_names.slice(0, 2).map((name, i) => (
                      <Link
                        key={name}
                        to={`/projects/${msg.project_ids?.[i] || ''}`}
                        className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700 hover:bg-gray-200"
                      >
                        {name}
                      </Link>
                    ))}
                    {msg.project_names.length > 2 ? (
                      <Badge size="sm" variant="gray">
                        +{msg.project_names.length - 2} more
                      </Badge>
                    ) : null}
                  </>
                ) : null}
              </div>

              {/* Message text */}
              <p className="mb-2 whitespace-pre-wrap text-sm text-gray-800">
                {msg.message_text || '(empty/non-text message)'}
              </p>

              {/* Footer: technical identifiers */}
              <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-400 font-mono">
                <span>chat_id: {msg.chat_id || '-'}</span>
                <span>msg_id: {msg.message_id || '-'}</span>
                <span>user_id: {msg.user_id || '-'}</span>
                {msg.content_type && (
                  <span>type: {msg.content_type}</span>
                )}
                {msg.api_id && (
                  <span>api: {msg.api_id}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default GlobalMessagesFeed;